import React, { useMemo } from "react";
import getCustomerCategories from "./CustomerCategories";
import Export from "../../../../components/Export";
import printChart from "../../../../PrintChart";
import {
  createCsvString,
  downloadCsv,
  exportItemsByCategoryXLSX,
} from "./Export";
import PageHeader from "../../../../components/PageHeader";

const getTopItems = (transactions, category) => {
  const customerPhones = getCustomerCategories(transactions)[category];
  let itemsSummary = {};

  transactions.forEach((transaction) => {
    if (customerPhones.includes(transaction.cust_phone)) {
      transaction.items?.forEach((item) => {
        const description = item.description;
        const quantity = parseInt(item.quantity, 10);

        itemsSummary[description] = (itemsSummary[description] || 0) + quantity;
      });
    }
  });

  return Object.entries(itemsSummary)
    .map(([description, quantity]) => ({ description, quantity }))
    .sort((a, b) => b.quantity - a.quantity)
    .slice(0, 5);
};

const ItemsTable = ({ items = [], title = "Top Items" }) => {
  const safeItems = Array.isArray(items) ? items : [];

  return (
    <div
      className="card border-0"
      style={{
        borderRadius: 16,
        boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        className="d-flex align-items-center justify-content-between px-3 px-md-4 py-3"
        style={{
          background:
            "linear-gradient(135deg, rgba(131,193,74,1) 0%, rgba(28,124,84,1) 100%)",
          color: "#fff",
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 12,
              backgroundColor: "rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i className="bi bi-box-seam" style={{ fontSize: 18 }} />
          </div>

          <div>
            <div style={{ fontWeight: 800, fontSize: 14, letterSpacing: 0.2 }}>
              {title}
            </div>
            <div style={{ fontSize: 12, opacity: 0.9 }}>
              Showing most purchased items
            </div>
          </div>
        </div>

        <span
          className="badge"
          style={{
            backgroundColor: "rgba(255,255,255,0.18)",
            border: "1px solid rgba(255,255,255,0.25)",
            padding: "8px 10px",
            borderRadius: 999,
            fontSize: 12,
          }}
        >
          {safeItems.length} item{safeItems.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Body */}
      <div className="table-responsive" style={{ maxHeight: 380 }}>
        <table className="table table-hover align-middle mb-0">
          <thead
            style={{
              position: "sticky",
              top: 0,
              zIndex: 2,
              backgroundColor: "#ffffff",
            }}
          >
            <tr>
              <th
                style={{
                  fontSize: 12,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  color: "#6b7280",
                  padding: "14px 16px",
                  borderBottom: "1px solid #e5e7eb",
                  width: 60,
                }}
              >
                Rank
              </th>
              <th
                style={{
                  fontSize: 12,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  color: "#6b7280",
                  padding: "14px 16px",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                Item
              </th>
              <th
                style={{
                  fontSize: 12,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  color: "#6b7280",
                  padding: "14px 16px",
                  borderBottom: "1px solid #e5e7eb",
                  textAlign: "right",
                  width: 140,
                }}
              >
                Count
              </th>
            </tr>
          </thead>

          <tbody>
            {safeItems.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center py-5">
                  <div style={{ color: "#9ca3af" }}>
                    <i className="bi bi-inbox" style={{ fontSize: 28 }} />
                    <div className="mt-2" style={{ fontWeight: 600 }}>
                      No items found
                    </div>
                    <div style={{ fontSize: 12 }}>
                      Try changing the date range or store filter.
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              safeItems.map((item, index) => {
                const rank = index + 1;
                const isTop3 = rank <= 3;

                return (
                  <tr
                    key={index}
                    style={{
                      backgroundColor:
                        index % 2 === 0 ? "rgba(249,250,251,0.7)" : "#fff",
                    }}
                  >
                    <td style={{ padding: "14px 16px" }}>
                      <span
                        className="badge"
                        style={{
                          borderRadius: 999,
                          padding: "8px 10px",
                          fontSize: 12,
                          fontWeight: 800,
                          backgroundColor: isTop3
                            ? "rgba(234,179,8,0.16)"
                            : "rgba(107,114,128,0.12)",
                          color: isTop3 ? "#92400e" : "#374151",
                          border: isTop3
                            ? "1px solid rgba(234,179,8,0.25)"
                            : "1px solid rgba(107,114,128,0.18)",
                        }}
                      >
                        #{rank}
                      </span>
                    </td>

                    <td style={{ padding: "14px 16px" }}>
                      <div className="d-flex align-items-center gap-2">
                        <div
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: 12,
                            backgroundColor: "rgba(131,193,74,0.12)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#1F4D2C",
                          }}
                        >
                          <i className="bi bi-bag-check" />
                        </div>

                        <div>
                          <div style={{ fontWeight: 700, color: "#111827" }}>
                            {item.description}
                          </div>
                          <div style={{ fontSize: 12, color: "#6b7280" }}>
                            Popular choice in this range
                          </div>
                        </div>
                      </div>
                    </td>

                    <td
                      style={{
                        padding: "14px 16px",
                        textAlign: "right",
                      }}
                    >
                      <span
                        className="badge"
                        style={{
                          backgroundColor: "rgba(31,77,44,0.10)",
                          color: "#1F4D2C",
                          border: "1px solid rgba(31,77,44,0.18)",
                          padding: "10px 12px",
                          borderRadius: 999,
                          fontWeight: 900,
                          fontSize: 12,
                        }}
                      >
                        {Number(item.quantity || 0).toLocaleString()}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
function Top5ProductsBuyPage({ transactions, storess, range }) {
  console.log("range", range);
  const customerCategories = useMemo(
    () => getCustomerCategories(transactions),
    [transactions],
  );

  const topItemsByCategory = useMemo(
    () =>
      Object.keys(customerCategories).reduce((acc, category) => {
        acc[category] = getTopItems(transactions, category);
        return acc;
      }, {}),
    [transactions],
  );

  // const exportCsv = () => {
  //   const csvString = createCsvString(topItemsByCategory);
  //   downloadCsv(csvString, "top_products.csv");
  // };
  const exportExcel = async () => {
    const from = range?.tran_date?.$gte
      ? new Date(range.tran_date.$gte).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : "N/A";

    const to = range?.tran_date?.$lte
      ? new Date(range.tran_date.$lte).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : "N/A";

    const formattedRange = `${from} - ${to}`;

    await exportItemsByCategoryXLSX({
      data: topItemsByCategory, // grouped by category
      storeName: storess || "All Stores",
      dateRange: formattedRange,
      fileName: "Top_Products_By_Customer_Category.xlsx",
      title: "Top 5 Products By Customer Count per Category",
    });
  };

  const chartId = 45;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-1 mt-4">
        <PageHeader
          title="Top 5 Products that Customers Buy"
          subtitle="Most purchased products by customers"
          icon="bi bi-box-seam"
        />

        <Export exportCsv={exportExcel} exportPdf={() => printChart(chartId)} />
      </div>
      <div className="overflow-auto" id="45">
        {Object.entries(topItemsByCategory).map(([category, items]) => (
          <div className="mb-4">
            <ItemsTable
              key={category}
              items={items}
              title={category.charAt(0).toUpperCase() + category.slice(1)}
            />
          </div>
        ))}
      </div>
    </>
  );
}

export default Top5ProductsBuyPage;
